import React from "react";
import { observer } from "mobx-react";
import {
  FormControlLabel,
  makeStyles,
  Popover,
  Switch,
  Typography
} from "@material-ui/core";
import locale from "@bonliva-crm/locale";
import { SpinnerOverlay } from "@components/SpinnerOverlay";
import { CustomerProfileStore } from "../CustomerProfileStore";
import DnDContainer from "@containers/DnDContainer/DnDContainer";
import { RegularButton } from "@components/buttons/RegularButton";
import { AlertOctagon } from "mdi-material-ui";
import { PrimaryButton } from "@components/buttons/PrimaryButton";
import { ThemeType } from "@theme";
import { transparentize } from "polished";
import { Info } from "@material-ui/icons";
import { Color } from "@bonliva-crm/ui";
import { WithTooltip } from "@common";

type Props = {
  store: CustomerProfileStore;
};

export const CustomerSettings = observer((props: Props) => {
  const { store } = props;
  const classes = useStyles({});

  return (
    <div className={classes.root}>
      <SpinnerOverlay show={store.isLoadingCustomerSettings} />
      <Popover
        open={store.openUploader}
        onClose={store.handleCloseUploader}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center"
        }}
        style={{ overflow: "visible" }}
      >
        <div className={classes.rootPopover}>
          <DnDContainer
            onDrop={(accepted) => {
              if (accepted[0]) {
                store.filesNoApproval = accepted[0];
              }
            }}
            className={classes.wrapper}
            overlayProps={{ className: classes.overlay }}
          >
            <Typography variant={"h5"}>
              {store.filesNoApproval?.name || locale.UploadCertificate}
            </Typography>
            <Typography variant={"body2"}>
              {store.filesNoApproval
                ? `${locale.FileSize}: ${store.filesNoApproval?.size}`
                : " "}
            </Typography>

            <div className={classes.btnWrap}>
              <RegularButton onClick={store.handleCloseUploader}>
                {locale.Cancel}
              </RegularButton>
              <RegularButton
                onClick={() => {
                  store.filesNoApproval = null;
                }}
                disabled={!store.filesNoApproval}
                variant={store.filesNoApproval ? "contained" : "text"}
              >
                {locale.Reset}
              </RegularButton>
              <RegularButton
                disabled={!!store.filesNoApproval}
                onClick={store.requestUpload}
                className={classes.uploadBtn}
                leftIcon={!store.filesNoApproval ? <AlertOctagon /> : undefined}
                variant={!store.filesNoApproval ? "contained" : "text"}
              >
                {locale.UploadFile}
              </RegularButton>
              <PrimaryButton
                disabled={!store.filesNoApproval}
                onClick={() => store.submitNoApprovalDocument()}
              >
                {locale.Save}
              </PrimaryButton>
            </div>
          </DnDContainer>
        </div>
      </Popover>

      <FormControlLabel
        control={
          <Switch
            disabled={
              store.isLoadingCustomerSettings ||
              store.profile?.needSignature ||
              store.profile?.needSignatureTROkInApp
            }
            checked={store.profile?.noApproval}
            onChange={(_, checked) => store.handleNoApprovalDocument(checked)}
            name="noApproval"
          />
        }
        label={locale.noNeedApproval}
      />
      <FormControlLabel
        control={
          <Switch
            disabled={
              store.isLoadingCustomerSettings ||
              store.profile?.noApproval ||
              store.profile?.needSignatureTROkInApp
            }
            checked={store.profile?.needSignature}
            onChange={(e, checked) =>
              store.toggleCustomerSettings(checked, e.target.name)
            }
            name="needSignature"
          />
        }
        label={locale.needSignature}
      />
      <FormControlLabel
        control={
          <Switch
            disabled={
              store.isLoadingCustomerSettings || store.isDisabledNeedSignatureTROkInApp
            }
            checked={store.profile?.needSignatureTROkInApp}
            onChange={(e, checked) =>
              store.toggleCustomerSettings(checked, e.target.name)
            }
            name="needSignatureTROkInApp"
          />
        }
        label={
          <WithTooltip
            placement={"top"}
            title={
              "När du väljer detta alternativet så tillåter vi konsulten att rapportera via appen. När konsulten skickar in sin tidrapport så fyller vi automatiskt i den tidrapportsmall som kunden kräver och skickar den till konsultens mail. Då kan konsulten sedan skriva ut ifylld mall och lämna in till kund för signering."
            }
          >
            <div>
              {locale.needSignatureTROkInApp}
              <Info style={{ marginLeft: 5, color: Color.DarkGray }} fontSize="small" />
            </div>
          </WithTooltip>
        }
      />
      <FormControlLabel
        control={
          <Switch
            disabled={
              store.isLoadingCustomerSettings || store.profile?.needSignatureTROkInApp
            }
            checked={store.profile?.noApprovalMonthly}
            onChange={(e, checked) =>
              store.toggleCustomerSettings(checked, e.target.name)
            }
            name="noApprovalMonthly"
          />
        }
        label={locale.noApprovalMonthly}
      />
      <FormControlLabel
        control={
          <Switch
            disabled={
              store.isLoadingCustomerSettings || store.profile?.needSignatureTROkInApp
            }
            checked={store.profile?.noApprovalShortening}
            onChange={(e, checked) =>
              store.toggleCustomerSettings(checked, e.target.name)
            }
            name="noApprovalShortening"
          />
        }
        label={locale.noApprovalShortening}
      />
      <FormControlLabel
        control={
          <Switch
            disabled={
              store.isLoadingCustomerSettings || store.profile?.needSignatureTROkInApp
            }
            checked={store.profile?.noApprovalSpecial}
            onChange={(e, checked) =>
              store.toggleCustomerSettings(checked, e.target.name)
            }
            name="noApprovalSpecial"
          />
        }
        label={locale.noApprovalSpecial}
      />
    </div>
  );
});

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: 16
  },
  rootPopover: {
    position: "relative",
    width: "40rem",
    minHeight: "80px"
  },
  btnWrap: {
    display: "flex",
    marginTop: "35px",
    justifyContent: "space-between"
  },
  uploadBtn: {
    "&:not(.Mui-disabled)": {
      background: transparentize(0.8, theme.palette.danger.main)
    }
  },
  wrapper: {
    display: "flex",
    flexDirection: "column",
    padding: 16,
    width: "100%"
  },

  overlay: {
    marginBottom: "0 !important"
  }
}));
